import { AccountingResult, AccountingSystem } from '@jms/model';
import { isConnectedAccountingIntegration } from './invoice.status.colour';

export const INVOICE_ACCOUNTING_RENDERER = 'invoice.accounting';

export interface InvoiceAccountingListCell {
  readonly kind: 'logo' | 'empty';
  readonly label: string;
  readonly logo: string | null;
}

const ACCOUNTING_LOGOS: Partial<Record<AccountingSystem, { readonly ok: string; readonly error: string }>> = {
  [AccountingSystem.Xero]: { ok: '/images/accounting/logo-xero-blue.svg', error: '/images/accounting/logo-xero-red.svg' },
  [AccountingSystem.MYOB]: { ok: '/images/accounting/logo-myob-only.svg', error: '/images/accounting/logo-myob-only-red.svg' },
  [AccountingSystem.QuickBooks]: { ok: '/images/accounting/logo-quickbooks.svg', error: '/images/accounting/logo-quickbooks-red.svg' },
  [AccountingSystem.MYOBAcumatica]: { ok: '/images/accounting/logo-acumatica.svg', error: '/images/accounting/logo-acumatica-red.svg' },
};

/** Provider name for the Accounting list cell tooltip / accessible name. */
export function invoiceAccountingProviderLabel(type: AccountingSystem | string | null | undefined): string | null {

  switch (type) {
    case AccountingSystem.Xero: return 'Xero';
    case AccountingSystem.MYOB: return 'MYOB';
    case AccountingSystem.QuickBooks: return 'QuickBooks';
    case AccountingSystem.MYOBAcumatica: return 'MYOB Acumatica';
    case AccountingSystem.Jobpac: return 'Jobpac';
    default: return null;
  }
}

/**
 * Accept the embedded result, a bare provider type, or nothing. Empty `{}` is unset — the
 * Details bind can land that way when Signal Forms seeds the EmbeddedEntity without `type`.
 */
export function invoiceAccountingResult(value: unknown): AccountingResult | null | undefined {

  if (value == null || value === '') return undefined;
  if (typeof value === 'string') return { type: value as AccountingSystem } as AccountingResult;
  if (typeof value !== 'object' || Array.isArray(value)) return undefined;
  const type = (value as { type?: unknown }).type;
  if (typeof type !== 'string' || !type) return undefined;
  return value as AccountingResult;
}

/** List / Details view of `integration`: connected-provider logo, or Not Set. */
export function invoiceAccountingListCell(integration: unknown): InvoiceAccountingListCell {

  const result = invoiceAccountingResult(integration);
  if (!isConnectedAccountingIntegration(result)) {
    return { kind: 'empty', label: 'Not Set', logo: null };
  }

  const label = invoiceAccountingProviderLabel(result.type) ?? String(result.type);
  const logos = ACCOUNTING_LOGOS[result.type];
  const logo = logos ? (result.success === false ? logos.error : logos.ok) : null;
  return logo ? { kind: 'logo', label, logo } : { kind: 'empty', label, logo: null };
}

/** Field value, then the live model — same fallback the list cell uses (`params.value ?? data.integration`). */
export function invoiceAccountingFieldValue(fieldValue: unknown, pageIntegration?: unknown): InvoiceAccountingListCell {

  return invoiceAccountingListCell(invoiceAccountingResult(fieldValue) ?? pageIntegration);
}

export interface InvoiceAccountingDetail {
  readonly provider: string;
  readonly logo: string | null;
  readonly text: string | null;
  readonly href: string | null;
  readonly failed: boolean;
}

/**
 * Details row beside the logo. A failed sync shows `error`. A synced document shows
 * referenceNumber, then the document number, then integrationInvoiceId. deepLink opens the ledger (WBS-12148).
 */
export function invoiceAccountingDetail(integration: unknown, documentNumber?: string | null): InvoiceAccountingDetail | null {

  const result = invoiceAccountingResult(integration);
  if (!result || !isConnectedAccountingIntegration(result)) return null;
  const provider = invoiceAccountingProviderLabel(result.type) ?? String(result.type);
  const logos = ACCOUNTING_LOGOS[result.type];
  const failed = result.success === false;
  const logo = logos ? (failed ? logos.error : logos.ok) : null;
  if (failed) {
    return { provider, logo, text: trimmed(result.error), href: null, failed: true };
  }
  return {
    provider,
    logo,
    text: trimmed(result.referenceNumber) ?? trimmed(documentNumber) ?? trimmed(result.integrationInvoiceId),
    href: trimmed(result.deepLink),
    failed: false,
  };
}

function trimmed(value: unknown): string | null {

  return typeof value === 'string' && value.trim() ? value.trim() : null;
}
